// Pure DOM focus helpers for failed-submission handling. Shared by the renderless
// FocusFirstInvalidField (primary submit) and any secondary action that gates on
// form validation before proceeding (e.g. "Send test"). See REACT_CONVENTIONS.md
// "Failed validation moves focus to the first invalid field".

const FOCUSABLE_CONTROL = 'input:not([type="hidden"]), textarea, select, [contenteditable="true"]';

const isHiddenInput = (el: HTMLElement): boolean =>
  el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'hidden';

// A react-select field carries its `name` on a hidden input, which can take neither
// focus nor scroll — hand off to the visible control in the same form field.
const resolveVisibleControl = (el: HTMLElement): HTMLElement => {
  if (!isHiddenInput(el)) return el;
  const fieldEl = el.closest<HTMLElement>('.MuiFormControl-root') || el.parentElement;
  return fieldEl?.querySelector<HTMLElement>(FOCUSABLE_CONTROL) || el;
};

// bodyHtml renders through ReactQuill whose editable surface has no `name` — fall
// back to the single editable Quill editor (the readOnly preview is excluded).
const resolveFieldElement = (formEl: HTMLElement, name: string): HTMLElement | null => {
  const named = formEl.querySelector<HTMLElement>(`[name="${name}"]`);
  if (named) return resolveVisibleControl(named);
  return formEl.querySelector<HTMLElement>('.ql-editor[contenteditable="true"]');
};

// Returns false when no invalid field could be reached, which means the submit
// is blocked by an error the user can neither see nor fix — never silently.
export const focusFirstInvalidField = (formEl: HTMLElement, errorNames: string[]): boolean => {
  const targets = errorNames
    .map((name) => resolveFieldElement(formEl, name))
    .filter((el): el is HTMLElement => !!el);
  if (!targets.length) {
    console.warn('focusFirstInvalidField: no field on screen for', errorNames.join(', '));
    return false;
  }
  // Focus whichever errored field sits first in document order — that order is
  // the on-screen order, so there is no separate field-ordering list to drift.
  const first = targets.reduce((earliest, el) =>
    (earliest.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING ? el : earliest)
  );
  // Suppress focus's own 'nearest' scroll (which parks the field under a sticky
  // footer) and center it in the scroll container so it's clearly visible.
  first.focus({ preventScroll: true });
  first.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return true;
};
